import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';
import QRCode from 'qrcode.react';
import styles from '../Button.module.less';

import config from '../../../layouts/ButtonPay/config';
import { logging } from '../../../actions/log';
import {
  setOrderInfo,
  setAntiInfo,
  setAntiErr,
  setPayPaySuc,
  setPayCheckIdSuc,
} from '../../../actions/buttonpay';
import { getPayGamePayList } from '../../../actions/native';
import { getLocalSoft } from '../../../actions/native';
import { PCStoreJsBridge } from '../../../nativeapi';
import { downloadAddSoft } from '../../../actions/downloadTask';
import { emptyLoginClose } from '../../../actions/user';
import native from '../../../reducers/native';
import Toast from '../../Toast/Toast';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectDownloadSofts = createSelector(
  state => state.downloadTask,
  downloadTask => downloadTask.softs,
);

const selectInstalledSoft = createSelector(
  state => state.native,
  native => native.installedSoft,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);

const selectIsEnabledSilent = createSelector(
  state => state.layout,
  layout => layout.isEnabledSilent,
);

const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

const selectPayGamePayList = createSelector(
  state => state.native,
  native => native.payGamePayList,
);

const selectButtonPay = createSelector(
  state => state.buttonpay,
  buttonpay => buttonpay,
);

const selectLoginClose = createSelector(
  state => state.user,
  user => user.loginBoxClose,
);

export default memo(({ soft, info = {}, logInfo = {} }) => {
  let cn_sbutton = styles.sbutton;
  let cn_sbutton_busing = styles.sbutton_busing;
  let cn_sbutton_open = styles.sbutton_open;
  // 按钮info传入百分比进度颜色
  let dl_progressColor = info.progressColor || '#CAF3E1';
  // 按钮info传入百分比进度将要覆盖的背景颜色
  let dl_backgroundColor = info.backgroundColor || '#ffffff00';
  if (info?.btnStyle === 'black') {
    cn_sbutton = styles.b_sbutton;
    cn_sbutton_busing = styles.b_sbutton_busing;
    cn_sbutton_open = styles.b_sbutton_open;
    dl_progressColor = info.progressColor || '#F2F2F2';
    dl_backgroundColor = info.backgroundColor || '#fff';
  }
  if (info?.btnStyle === 'opacity') {
    cn_sbutton = styles.g_sbutton;
    cn_sbutton_busing = styles.g_sbutton_busing;
    cn_sbutton_open = styles.g_sbutton_open;
    dl_progressColor = info.progressColor || '#ffffff33';
    dl_backgroundColor = info.backgroundColor || 'transparent';
  }
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const logInfoData = useSelector(selectLogInfo);
  const payList = useSelector(selectPayGamePayList); // 正在下载
  const downloadSofts = useSelector(selectDownloadSofts); // 正在下载
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const installedSofts = useSelector(selectInstalledSoft); // 已经安装
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const isEnabledSilent = useSelector(selectIsEnabledSilent); // 是否打开快速静默安装

  const { btnPayPaySuc, btnPayAntiSuc } = useSelector(selectButtonPay);

  const [isDownloadEnd, setIsDownloadEnd] = useState(false);
  const [loginNeedOrdering, setloginNeedOrdering] = useState(false);
  const [ordering, setOrdering] = useState(1);
  const [fondSoft, setFondSoft] = useState(undefined);
  const [upgrade, setUpgrade] = useState(undefined);
  const [silentSoft, setSilentSoft] = useState(undefined);
  const [installed, setInstalled] = useState(undefined);
  const [payGamePayed, setPayGamePayed] = useState(undefined);
  const [linearGradient, setLinearGradient] = useState(
    `linear-gradient(to right, ${dl_progressColor} 0%, ${dl_backgroundColor} 0%)`,
  );
  const [buttonText, setButtonText] = useState(info.text || '购买');
  const loginBoxClose = useSelector(selectLoginClose);

  // 未登录就点击购买，登录成功后再次触发点击
  useEffect(() => {
    if (userInfo && userInfo?.st) {
      loginToPaySoft();
    }
    if (loginNeedOrdering && userInfo && !userInfo?.st && loginBoxClose?.result === 0) {
      setloginNeedOrdering(false);
    }
  }, [userInfo, loginBoxClose]);

  const loginToPaySoft = useCallback(() => {
    if (loginNeedOrdering && userInfo && userInfo?.st && loginBoxClose?.result === 1) {
      setTimeout(() => {
        handleSoft();
      }, 300);
      setloginNeedOrdering(false);
    }
  }, [loginNeedOrdering, userInfo, loginBoxClose]);

  // 认证成功之后开始重新下单
  useEffect(() => {
    if (
      soft &&
      btnPayAntiSuc &&
      btnPayAntiSuc?.soft &&
      btnPayAntiSuc?.soft?.softID === soft?.softID &&
      btnPayAntiSuc?.result
    ) {
      checkUserIdSuccess();
    }
  }, [soft, btnPayAntiSuc]);

  // 支付成功之后开始下载
  useEffect(() => {
    if (
      soft &&
      btnPayPaySuc &&
      btnPayPaySuc?.soft &&
      btnPayPaySuc?.soft?.softID === soft?.softID &&
      btnPayPaySuc?.result &&
      btnPayPaySuc?.result?.payStatus === 1 &&
      !isDownloadEnd
    ) {
      handleOrderPaySuccess();
    }
  }, [soft, btnPayPaySuc, isDownloadEnd]);

  // 查询当前是否已经购买
  useEffect(() => {
    if (payList && payList.length > 0) {
      setPayGamePayed(payList.find(softID => softID && softID === soft.softID));
    } else {
      setPayGamePayed(undefined);
    }
  }, [soft, payList]);

  // 查询当前是否已经安装
  useEffect(() => {
    if (upgradeSofts) {
      setUpgrade(
        upgradeSofts.find(
          soft_ =>
            soft_.androidType !== '1' &&
            (soft_.softID === soft.softID || soft_.SoftID === soft.softID),
        ),
      );
    }
  }, [soft, upgradeSofts]);

  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (installedSofts) {
      setInstalled(
        installedSofts.find(
          soft_ =>
            soft_.androidType !== '1' &&
            (soft_.softID === soft.softID || soft_.SoftID === soft.softID),
        ),
      );
    }
  }, [soft, installedSofts]);

  // 查询是否打开静默安装的开关
  // 查询当前是否已经安装 并且是否可升级
  useEffect(() => {
    if (silentInstSoft && isEnabledSilent) {
      setSilentSoft(silentInstSoft.find(soft_ => soft_.softID === soft.softID));
    }
  }, [soft, silentInstSoft, isEnabledSilent]);

  // 更新按钮显示状态
  useEffect(() => {
    let linear_Gradient = `linear-gradient(to right, ${dl_backgroundColor} 0%, ${dl_backgroundColor} 0%)`;
    let button_Text = info?.text?.indexOf('立即') > -1 ? '立即购买' : '购买';
    const found =
      downloadSofts &&
      downloadSofts.find(
        soft_ =>
          soft_.type === 'pc' &&
          (soft_.pc.SoftID === soft.softID ||
            soft_.pc.SoftID === soft.SoftID ||
            soft_.pc.SoftID === soft.softId),
      );
    // 正在任务中...
    if (found && found.pc && found.pc.SoftID) {
      if (found.attr.status === 'pre') {
        button_Text = '准备中';
      } else if (found.attr.status === 'wait') {
        button_Text = '等待中';
      } else if (found.attr.status === 'pause') {
        button_Text = '继续';
        linear_Gradient = `linear-gradient(to right, ${dl_progressColor} ${found.attr.progress ||
          0}%, ${dl_backgroundColor} ${found.attr.progress || 0}%)`;
      } else if (found.attr.status === 'dl') {
        button_Text = `${found.attr && found.attr.progress ? found.attr.progress.toFixed(0) : 0}%`;
        linear_Gradient = `linear-gradient(to right, ${dl_progressColor} ${found.attr.progress ||
          0}%, ${dl_backgroundColor} ${found.attr.progress || 0}%)`;
      } else if (found.attr.status === 'i') {
        button_Text = '安装中';
        linear_Gradient = `linear-gradient(to right, ${dl_progressColor} 0%, ${dl_backgroundColor} 0%)`;
        setIsDownloadEnd(true);
      } else if (found.attr.status === 'exitq') {
        // 安装完成重新获取一次本地列表
        // dispatch(getLocalSoft());
      } else if (found.attr.status === 'err') {
      }
    } else if (upgrade) {
      button_Text = '升级';
    } else if (installed) {
      button_Text = '打开';
    } else {
      // 当不是（任务中、升级、打开）的时候，判断是否以及购买，如何已经购买就显示安装，
      if (payGamePayed) {
        button_Text = info?.text?.indexOf('立即') > -1 ? '立即安装' : '安装';
        // 如何已经购买就显示安装，并且是快装软件则显示快装。
        if (silentSoft && isEnabledSilent) {
          button_Text = '快装';
        }
      } else {
        button_Text = info?.text?.indexOf('立即') > -1 ? '立即购买' : '购买';
      }
    }

    if (buttonText !== button_Text) {
      setButtonText(button_Text);
    }
    if (linearGradient !== linear_Gradient) {
      setLinearGradient(linear_Gradient);
    }
    setFondSoft(found || undefined);
  }, [
    dispatch,
    soft,
    downloadSofts,
    payGamePayed,
    upgrade,
    installed,
    silentSoft,
    isEnabledSilent,
  ]);

  // 获取生成打桩参数
  const getLogParam2 = useCallback(
    (soft_info, logInfo_para) => {
      const logParam2 = {
        source: logInfo_para.source,
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: soft_info.softID,
        softName: soft_info.softName,
        softType: '12', // 0: PC应用 1： 安卓  10：外链购买   11：iframe购买  12：单机付费购买
        type: upgrade ? 'u' : 'd', // 更新还是下载
        url: soft_info.downLoadUrl,
      };
      // 当为搜索结果也跳转过来的，带上附加信息
      // search_type 1: 热词  2: 联想词  3: 搜索词  4: 联想词 首位应用  5: 热词应用  6: 联想词 非首位应用
      if (logInfo_para.isSearch) {
        logParam2.search_type = logInfoData.search_type;
        logParam2.search_word = logInfoData.search_word;
        logParam2.lenovo_bizinfo = logInfo_para.lenovo_bizinfo || logInfoData.lenovo_bizinfo;
      }
      // 联想词softcard  做特殊处理 参数通过 logInfo (即logInfo_para) 传入
      if (
        logInfo_para.isSearch &&
        (logInfo_para.search_type === 4 ||
          logInfo_para.search_type === 5 ||
          logInfo_para.search_type === 6)
      ) {
        logParam2.source_p = logInfo_para.source_p;
        logParam2.source_pp = logInfo_para.source_pp;
        logParam2.search_type = logInfo_para.search_type;
        logParam2.search_word = logInfo_para.search_word;
        logParam2.lenovo_bizinfo = logInfo_para.lenovo_bizinfo || logInfoData.lenovo_bizinfo;
      }
      // 处理命令行直接跳入搜索结果页面没有search_word的问题
      if (logInfo_para.isSearch && logInfo_para.search_word) {
        logParam2.search_word = logInfo_para.search_word;
      }
      return logParam2;
    },
    [logInfoData, upgrade],
  );

  // 点击按钮处理
  const handleInstallSoft = useCallback(
    (soft_info, button_Text, logInfo_para, fond_soft) => {
      if (!soft_info.softID) {
        return;
      }
      const logParam2 = getLogParam2(soft_info, logInfo_para);
      // 获取静默安装参数
      let installparams = null;
      if (isEnabledSilent && silentSoft && silentSoft.softID === soft_info.softID) {
        installparams = {};
        logParam2.type = upgrade ? 'xu' : 'xd';
        if (silentSoft.silentParamDic) {
          installparams.quiet = silentSoft.silentParamDic;
        }
        if (silentSoft.silentDirParamDic) {
          installparams.todir = silentSoft.silentDirParamDic;
        }
      }

      let isNeedLog = false;
      const softInfo = { ...soft_info, logInfo: logParam2, installparams, isClick: 'click' };
      // 正常安装
      if (
        button_Text === info.text ||
        button_Text === '购买' ||
        button_Text === '安装' ||
        button_Text === '升级' ||
        button_Text === '快装'
      ) {
        // 添加下载任务
        dispatch(downloadAddSoft(softInfo));
        isNeedLog = true;
      }

      // // 正在下载中，点击暂停
      // if (fond_soft && fond_soft.attr && fond_soft.attr.status === 'dl') {
      //   logParam2.isPause = '1';
      //   PCStoreJsBridge.downloadPause({ ...fond_soft });
      //   isNeedLog = true;
      // }

      // 正在暂停中，点击继续
      if (fond_soft && fond_soft.attr && fond_soft.attr.status === 'pause') {
        logParam2.isPause = '0';
        PCStoreJsBridge.downloadResume({ ...fond_soft });
        isNeedLog = true;
      }
      if (button_Text === '打开') {
        logParam2.type = 'o';
        PCStoreJsBridge.openSoft(installed);
        isNeedLog = true;
      }

      // 按钮点击打桩
      if (isNeedLog) {
        dispatch(
          logging({
            category: 'C',
            action: 'Install',
            label: '',
            param2: logParam2,
            param3: soft_info.bizInfo || null,
          }),
        );
      }
    },
    [dispatch, installed, silentSoft, isEnabledSilent],
  );

  const handleSoft = useCallback(async () => {
    if (!userInfo || !userInfo.st) {
      setloginNeedOrdering(true);
      window.CallNativeMethod('login'); // 未登录 调用登录弹窗
      return;
    }

    if (
      buttonText.indexOf('安装') > -1 ||
      buttonText.indexOf('快装') > -1 ||
      buttonText === '升级' ||
      buttonText === '打开'
    ) {
      handleInstallSoft(soft, buttonText, logInfo, fondSoft);
      return;
    }
    loggingOrder('C', 'PayGame_Button');
    // 开始创建订单
    createOrder();
  }, [userInfo, soft, buttonText, logInfo, fondSoft]);

  const createOrder = useCallback(async () => {
    if (ordering !== 1) {
      return;
    }
    try {
      setOrdering(2);
      const res = await config.createOrder({
        softId: soft.softID || soft.softId,
      });
      setOrdering(1);
      if (res?.status === 0 && res?.data) {
        dispatch(
          setOrderInfo({
            show: true,
            soft: soft,
            logInfo,
            orderInfo: res?.data,
            orderPayResult: '',
          }),
        );
        // 下单成功
        loggingOrder('R', 'PayGame_Order_Success');
      } else if (res?.status === -3) {
        dispatch(
          setAntiErr({
            show: true,
            soft: soft,
            logInfo,
          }),
        );
        // -3：未成年账号 付款限制金额
        loggingOrder('R', 'PayGame_Order_Payment_Limit');
      } else if (res?.status === -4) {
        dispatch(
          setAntiInfo({
            show: true,
            soft: soft,
            logInfo,
            orderInfo: res?.data,
          }),
        );
        // -4：未实名认证
        loggingOrder('R', 'PayGame_Order_Certification');
      } else if (res?.status === -5) {
        // -5：游戏重复购买, 当着购买成功处理
        // Toast.error({ content: '重复购买！' });
        loggingOrder('R', 'PayGame_Order_Buy_Again');
        handleOrderPaySuccess();
      } else {
        // 其他失败
        Toast.error({ content: '操作失败！' });
        loggingOrder('R', 'PayGame_Order_Error');
      }
    } catch (err) {
      setOrdering(1);
      // 请求失败
      Toast.error({ content: '操作失败！' });
      loggingOrder('R', 'PayGame_Order_Error');
    }
  }, [ordering, soft, logInfo]);

  // 下订单成功，支付成功后，自动触发下载软件
  const handleOrderPaySuccess = useCallback(() => {
    dispatch(getPayGamePayList());
    handleInstallSoft(soft, buttonText, logInfo, fondSoft);
  }, [soft, buttonText, logInfo, fondSoft]);

  // 身份认证成功后，再次调用点击按钮函数，触发下订单
  const checkUserIdSuccess = useCallback(() => {
    setTimeout(() => {
      handleSoft();
    }, 500);
  }, []);

  const loggingOrder = useCallback(
    (category, action) => {
      dispatch(
        logging({
          category: category || 'C',
          action: action || 'PayGame_Button',
          label: '',
          param2: {
            source: logInfo.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            softid: soft.softID,
            softName: soft.softName,
            softType: '0',
          },
          param3: soft.bizInfo || null,
        }),
      );
    },
    [soft, logInfo, logInfoData, upgrade],
  );

  return (
    <>
      <div
        style={
          fondSoft && fondSoft.attr && fondSoft.attr.status && linearGradient
            ? { backgroundImage: linearGradient }
            : {}
        }
        className={
          fondSoft && fondSoft.attr
            ? `${styles.button} ${cn_sbutton_busing} ${info.className_busing || ''}`
            : `${styles.button}
            ${cn_sbutton}
            ${info.className || ''}
            ${buttonText === '打开' && `${cn_sbutton_open} ${info && info.className_open}`}`
        }
        onClick={e => {
          // 当为购买与打开的时候，不向上冒泡，主要处理打开和购买的时候搜索结果出推荐
          // if (!payGamePayed || (!upgrade && installed)) {
          //   e.stopPropagation();
          // }
          handleSoft();
        }}
      >
        {buttonText}
      </div>
    </>
  );
});
